import { supabase } from "@/lib/supabase";

interface SubmissionEmailData {
  userId: string;
  userName: string;
  userEmail: string;
  carrierName: string;
  vehicleInfo: string;
  premiumAmount?: number;
  premiumFrequency?: string;
  submissionId: string;
}

async function readErrorBody(response: Response): Promise<string> {
  try {
    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

export async function sendUserConfirmationEmail(data: SubmissionEmailData): Promise<void> {
  const response = await fetch("/api/email/user-confirmation", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      to: data.userEmail,
      userName: data.userName,
      carrierName: data.carrierName,
      vehicleInfo: data.vehicleInfo,
      submissionId: data.submissionId,
    }),
  });
  if (!response.ok) {
    throw new Error(`user-confirmation ${response.status}: ${await readErrorBody(response)}`);
  }
}

export async function sendAdminAlertEmail(data: SubmissionEmailData): Promise<void> {
  const response = await fetch("/api/email/admin-alert", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      userName: data.userName,
      userEmail: data.userEmail,
      carrierName: data.carrierName,
      vehicleInfo: data.vehicleInfo,
      premiumAmount: data.premiumAmount,
      premiumFrequency: data.premiumFrequency,
      submissionId: data.submissionId,
    }),
  });
  if (!response.ok) {
    throw new Error(`admin-alert ${response.status}: ${await readErrorBody(response)}`);
  }
}

// -----------------------------------------------------------------------------
// Referral program: stage-transition notifications (R1..R6).
// Each state change in the referrals table corresponds to one email to the
// referrer. The server-side /api/email/referral endpoint owns the actual
// templates; this client-side helper only marshals context and fires it off.
// -----------------------------------------------------------------------------

export type ReferralStage =
  | "signed_up"       // R1 - friend just submitted the intake form
  | "savings_quoted"  // R2 - audit completed, savings and commission known
  | "friend_paid"     // R3 - friend paid, 30-day hold begins
  | "qualified"       // R4 - hold cleared, ready to claim
  | "paid"            // R5 - payout sent, receipt
  | "void";           // R6 - friend refunded or never converted

export interface ReferralEmailContext {
  referrerEmail: string;
  referrerName: string;
  referrerCode: string;
  // Passed to the server endpoint so it can stamp last_notified_stage on the
  // matching rows after a successful send. Leaving this out means the daily
  // reconciliation cron may re-send this email once.
  referralIds?: string[];
  referredFirstName?: string;
  estimatedSavingsUsd?: number;
  commissionUsd?: number;
  availableUsd?: number;
  claimUrl?: string;
  holdClearsOn?: string;        // ISO date
  payoutMethod?: string;        // 'zelle' | 'venmo' | 'paypal' | ...
  payoutHandleMasked?: string;  // e.g. "alex@..." or "@ale..."
  payoutReference?: string;
  coveredReferrals?: Array<{ name: string; referredOn: string; amount: number }>;
  lifetimeReferred?: number;
  lifetimeEarnedUsd?: number;
  voidReason?: string;
  pipeline?: Array<{ name: string; stage: string; amount: number; clearsOn?: string }>;
}

export async function sendReferralStageEmail(
  stage: ReferralStage,
  context: ReferralEmailContext
): Promise<void> {
  const response = await fetch("/api/email/referral", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ stage, ...context }),
  });
  if (!response.ok) {
    throw new Error(`referral(${stage}) ${response.status}: ${await readErrorBody(response)}`);
  }
}

/**
 * User-scoped "resend my confirmation email" — called from the success
 * screen banner when the initial send failed.
 */
export async function resendUserConfirmation(submissionId: string): Promise<void> {
  const { data: sessionResp } = await supabase.auth.getSession();
  const token = sessionResp.session?.access_token;
  if (!token) throw new Error("Not signed in");

  const response = await fetch("/api/email/resend-user-confirmation", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ submissionId }),
  });
  if (!response.ok) {
    throw new Error(`resend-user-confirmation ${response.status}: ${await readErrorBody(response)}`);
  }
}
